import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { HStack } from "@design-system/HStack/HStack";
import { Modal, StandardModalHeader } from "@design-system/Modal/Modal";
import { VStack } from "@design-system/VStack/VStack";
import { ClientField } from "./AssignmentFields";
import {
    CLIENT_EDITABLE_UNTIL_ENTITY_HELPER,
    clientAssignmentModalTitle,
} from "./assignmentCopy";
import { EntityAssignmentModal } from "./EntityAssignmentModal";
import { useOrganizationIntegrations } from "./OrganizationIntegrationsContext";
import { assignmentCapReason } from "./useCommitAssignments";
import styles from "./IntegrationsPage.module.scss";

interface ClientAssignmentModalProps {
    /** The accounts this confirm applies to — one row, or a whole selection. */
    accountIds: number[];
    /** The client to start on, for accounts that already have one staged. */
    initialWorkspaceId: string | null;
    onClose: () => void;
    /** Receives the accounts that actually persisted, if any did. */
    onAssigned?: (accountIds: number[]) => void;
}

/**
 * Picks the client a set of accounts belongs to, then hands straight over to
 * the entity step.
 *
 * A client can't be persisted on its own — the API assigns by `entityId` — so
 * confirming stages it and opens the entity modal on the same accounts rather
 * than leaving the user with a half-finished assignment. Dismissing stages
 * nothing: the pick is only committed through Confirm.
 */
export const ClientAssignmentModal = ({
    accountIds,
    initialWorkspaceId,
    onClose,
    onAssigned,
}: ClientAssignmentModalProps) => {
    const { workspaces, setPendingClient } = useOrganizationIntegrations();
    const [workspaceId, setWorkspaceId] = useState<string | null>(
        initialWorkspaceId,
    );
    const [confirmedWorkspaceId, setConfirmedWorkspaceId] = useState<
        string | null
    >(null);

    const blockedReason = assignmentCapReason(accountIds.length);

    const handleConfirm = () => {
        if (workspaceId === null || blockedReason !== undefined) {
            return;
        }
        for (const accountId of accountIds) {
            setPendingClient(accountId, workspaceId);
        }
        setConfirmedWorkspaceId(workspaceId);
    };

    if (confirmedWorkspaceId !== null) {
        return (
            <EntityAssignmentModal
                accountIds={accountIds}
                workspaceId={confirmedWorkspaceId}
                onClose={onClose}
                onAssigned={onAssigned}
            />
        );
    }

    return (
        <Modal
            show
            variant="plain"
            dialogClassName={styles.assignmentDialog}
            onClose={onClose}
        >
            <StandardModalHeader>
                {clientAssignmentModalTitle(accountIds.length)}
            </StandardModalHeader>
            <VStack gap={24}>
                <VStack gap={16}>
                    <span className={styles.confirmHelper}>
                        {CLIENT_EDITABLE_UNTIL_ENTITY_HELPER}
                    </span>
                    <ClientField
                        workspaces={workspaces}
                        workspaceId={workspaceId}
                        onChange={setWorkspaceId}
                    />
                </VStack>
                <HStack justify="end">
                    <WithHotkey
                        hotKey="mod+enter"
                        hotKeyDisplayPosition="inline"
                        tooltip={blockedReason}
                        hotKeyOptions={{
                            description: "Confirm client assignment",
                            preventDefault: true,
                        }}
                    >
                        {(hotkeyHint) => (
                            <Button
                                variant="primary"
                                size="md"
                                onClick={handleConfirm}
                                disabled={
                                    workspaceId === null ||
                                    blockedReason !== undefined
                                }
                                rightIcon={hotkeyHint}
                            >
                                Confirm
                            </Button>
                        )}
                    </WithHotkey>
                </HStack>
            </VStack>
        </Modal>
    );
};
